import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import lunr from 'lunr';

import { LoadingIndicatorForApp, StatusPage } from 'components';
import {
  BookModifiersContext,
  BookValuesContext,
  NetworkStatusValuesContext,
} from 'contexts';
import BOOK_QUERY from 'graphql/queries/BookQuery.graphql';
import MOST_RECENT_BOOK_LOCATION from 'graphql/queries/MostRecentBookLocationQuery.graphql';
import { useQueryOffline } from 'hooks';
import { Book, BookDocumentInformation } from 'types';
import { csrfToken, getLocalSettingsProxy } from 'utilities';
import { getMostRecentBookPk } from 'utilities/mostRecentBookLocationUtilities';

/**
 * The purpose of this provider is to expose a Book object that represents the current title.
 * If the current title is unknown, it will find the most recent title from the user's reading history.
 */

export const BookProvider = ({ bookPk: constructorBookPk, children }) => {
  // The book provider can be passed an bookPk or find one in the url path.
  const urlBookPk = parseInt(useParams().bookPk);
  const { isOffline, isOnline } = useContext(NetworkStatusValuesContext);
  const [bookPk, setBookPk] = useState(constructorBookPk || urlBookPk);

  const [book, setBook] = useState(null);
  const [idle, setIdle] = useState(false);

  const bookDocumentInformations = useRef([]);

  const titleAccessedEndpointPinged = useRef(false);

  const lunrSearchIndexPromise = useRef(false);

  /**
   * If an ePub ID is not available, we can check the user's last viewed location for any title,
   * which will include the last viewed ePub ID.
   */
  const {
    loading: bookLocationLoading,
    error: bookLocationError,
    data: { epubLocationHistoryLatestByCurrentUser: bookLocationData } = {},
  } = useQuery(MOST_RECENT_BOOK_LOCATION, {
    skip: Boolean(bookPk) || isOffline,
  });

  useEffect(() => {
    if (bookLocationLoading || bookPk) {
      return;
    }

    const foundBookPk = getMostRecentBookPk(bookLocationData);

    if (!foundBookPk) {
      window.location = '//redshelf.com';

      return;
    }

    setBookPk(foundBookPk);
  }, [bookLocationLoading, bookLocationData, bookPk]);

  /**
   * As long as an ePub ID is available, the provider can query for the book information.
   */
  const {
    loading: bookLoading,
    error: bookError,
    data: bookData,
    refetch,
  } = useQueryOffline(BOOK_QUERY, {
    variables: {
      epubId: bookPk,
    },
    skip: !bookPk,
  });

  useEffect(
    function pingTitleAccessedEndpoint() {
      if (!bookPk || bookLoading || !bookData) {
        return;
      }

      const book = Book.createFromData(bookData);
      setBook(book);

      // Add debug references
      window.v7.book = book;
      window.v7.localSettings = getLocalSettingsProxy(book.pk);

      if (titleAccessedEndpointPinged.current) {
        return;
      }

      // Once the book is loaded, ping the title-accessed endpoint
      // when this endpoint is hit, the backend will index the book for search if it is not already indexed
      try {
        if (isOnline) {
          fetch('/api/title-accessed', {
            method: 'POST',
            body: `epub_id=${bookPk}`,
            headers: {
              'Content-Type':
                'application/x-www-form-urlencoded; charset=UTF-8',
              'X-CSRFToken': csrfToken(),
            },
          });

          titleAccessedEndpointPinged.current = true;
        }
      } catch (e) {
        console.error('Attempt to reach title-accessed endpoint failed.', e);
      }

      setIdle(true);
    },
    [bookData, bookLoading, bookPk, isOnline],
  );

  const reloadBook = useCallback(async () => {
    setIdle(false);
    const result = await refetch();
    setBook(Book.createFromData(result?.data));
    setIdle(true);
  }, [refetch]);

  const getDocumentInformation = useCallback(
    async (
      documentIndex,
      frameDocument,
      mode = BookDocumentInformation.MODES.NO_DOM,
    ) => {
      let bookDocumentInformation =
        bookDocumentInformations.current[documentIndex];

      /**
       * If the requested mode involves no DOM references,
       * and a cached object for the document already exists,
       * return the cached object.
       *
       * This allows avoiding re-computing the book information for documents that have already been visited in some cases.
       *
       * If information for a given document has already been computed (for TTS),
       * even if DOM references are lost due to garbage collection,
       * the already-computed word objects can still be used for search (which does not require the DOM references).
       *
       * Inversely, if an instance was computed with no DOM (for search), and is now needed again but with more context (for TTS),
       * it must be recomputed. So this only works when requesting an instance with no DOM computations.
       */
      if (
        mode === BookDocumentInformation.MODES.NO_DOM &&
        bookDocumentInformation
      ) {
        return await bookDocumentInformation;
      }

      bookDocumentInformations.current[documentIndex] = new Promise(
        async (resolve) => {
          bookDocumentInformation = frameDocument
            ? new BookDocumentInformation(
                frameDocument,
                book.documents[documentIndex],
              )
            : await BookDocumentInformation.createFromBookDocument(
                book.documents[documentIndex],
              );

          bookDocumentInformation.buildWordObjects(mode);
          bookDocumentInformation.constructor.cleanup();

          resolve(bookDocumentInformation);
        },
      );

      return await bookDocumentInformations.current[documentIndex];
    },
    [book],
  );

  const getLunrSearchIndex = useCallback(async () => {
    if (!book.hasLunrSearchIndex) {
      return false;
    }

    if (!lunrSearchIndexPromise.current) {
      lunrSearchIndexPromise.current = new Promise(async (resolve, reject) => {
        let response, responseJson, responseText, searchIndex;

        try {
          response = await fetch(`/search/${book.pk}`);
        } catch (e) {
          console.error(e);
          reject(new Error('Failed to fetch Lunr search index.'));
          return;
        }

        try {
          responseText = await response.text();
          responseJson = JSON.parse(responseText);
          searchIndex = lunr.Index.load(responseJson);
        } catch (e) {
          console.error(e);
          reject(new Error('Failed to parse Lunr search index.'));
          return;
        }

        resolve(searchIndex);
      });
    }

    return await lunrSearchIndexPromise.current;
  }, [book]);

  const values = useMemo(
    () => ({
      book,
      bookPk,
      bookProviderIdle: idle,
      getDocumentInformation,
      getLunrSearchIndex,
    }),
    [book, bookPk, idle, getDocumentInformation, getLunrSearchIndex],
  );

  const modifiers = useMemo(
    () => ({
      reloadBook,
    }),
    [reloadBook],
  );

  const error = bookError ?? bookLocationError;
  if (error) {
    return (
      <StatusPage statusCode={error.networkError?.statusCode} bookPk={bookPk} />
    );
  }

  return (
    <BookValuesContext.Provider value={values}>
      <BookModifiersContext.Provider value={modifiers}>
        {book ? children : <LoadingIndicatorForApp />}
      </BookModifiersContext.Provider>
    </BookValuesContext.Provider>
  );
};

export default BookProvider;
